import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { ScrollArea } from './ui/scroll-area';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { 
  Inbox, X, Send, Loader2, MessageSquare, ArrowLeft, Clock, User, Mail, CheckCheck
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to get full avatar URL
const getAvatarUrl = (avatarUrl) => {
  if (!avatarUrl) return null;
  if (avatarUrl.startsWith('http')) return avatarUrl;
  return `${BACKEND_URL}${avatarUrl}`;
};

const formatTimeAgo = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const diff = Math.floor((now - date) / 1000);
  
  if (diff < 60) return 'Just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return date.toLocaleDateString();
};

export const AdminInbox = () => {
  const { user, token } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const messagesEndRef = useRef(null);
  
  // Check if user is admin (use role or is_admin flag, not hardcoded email)
  const isAdmin = user?.role === 'admin' || user?.is_admin === true;
  
  // Fetch unread count
  useEffect(() => {
    if (token && isAdmin) {
      fetchUnreadCount();
      const interval = setInterval(fetchUnreadCount, 30000);
      return () => clearInterval(interval);
    }
  }, [token, isAdmin]);
  
  // Fetch conversations when modal opens
  useEffect(() => {
    if (isOpen && token && isAdmin) {
      fetchConversations();
    }
  }, [isOpen, token, isAdmin]);
  
  // Scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);
  
  const fetchUnreadCount = async () => {
    try {
      const response = await axios.get(`${API}/messages/unread-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error('Failed to fetch unread count');
    }
  };
  
  const fetchConversations = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/messages/inbox`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConversations(response.data.conversations || []);
    } catch (error) {
      console.error('Failed to fetch inbox');
    } finally {
      setLoading(false);
    }
  };
  
  const selectConversation = async (conv) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/messages/conversation/${conv.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedConversation({
        ...conv,
        user: response.data.user
      });
      setMessages(response.data.messages || []);
      fetchConversations(); // Refresh to update unread counts
      fetchUnreadCount();
    } catch (error) {
      toast.error('Failed to load conversation');
    } finally {
      setLoading(false);
    }
  };
  
  const handleReply = async () => {
    if (!reply.trim() || !selectedConversation) return;
    
    setSending(true);
    try {
      await axios.post(
        `${API}/admin/messages/reply/${selectedConversation.id}`,
        { message: reply.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Reply sent!');
      setReply('');
      selectConversation(selectedConversation);
    } catch (error) {
      toast.error('Failed to send reply');
    } finally {
      setSending(false);
    }
  };
  
  if (!isAdmin) return null;
  
  return (
    <>
      {/* Floating Inbox Button */}
      <motion.button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-20 left-6 z-[9998] w-12 h-12 rounded-full bg-gradient-to-br from-babylon-gold to-babylon-bronze flex items-center justify-center shadow-lg shadow-babylon-gold/30 hover:shadow-babylon-gold/50"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        title="Admin Inbox"
      >
        <Inbox className="w-5 h-5 text-babylon-void" />
        {unreadCount > 0 && (
          <motion.span 
            initial={{ scale: 0 }}
            animate={{ scale: [1, 1.2, 1] }}
            transition={{ repeat: Infinity, duration: 2 }}
            className="absolute -top-1 -right-1 w-5 h-5 bg-babylon-blood rounded-full text-[10px] text-white flex items-center justify-center font-bold"
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </motion.span>
        )}
      </motion.button>
      
      {/* Inbox Modal */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999] flex items-center justify-center p-4"
            onClick={() => { setIsOpen(false); setSelectedConversation(null); }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="w-full max-w-2xl h-[80vh] bg-gradient-to-br from-babylon-obsidian to-babylon-shadow border border-babylon-gold/20 rounded-xl overflow-hidden shadow-2xl flex flex-col"
              onClick={e => e.stopPropagation()}
            >
              {/* Header */}
              <div className="p-4 border-b border-babylon-gold/20 flex items-center justify-between shrink-0">
                <div className="flex items-center gap-3">
                  {selectedConversation && (
                    <button 
                      onClick={() => setSelectedConversation(null)}
                      className="p-2 hover:bg-babylon-gold/10 rounded-lg"
                    >
                      <ArrowLeft size={18} className="text-babylon-text-dim" />
                    </button>
                  )}
                  <div className="p-2 bg-babylon-gold/20 rounded-lg">
                    <Inbox className="w-5 h-5 text-babylon-gold" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg text-babylon-gold">
                      {selectedConversation ? selectedConversation.user?.name : 'Admin Inbox'}
                    </h3>
                    <p className="text-xs text-babylon-text-dim">
                      {selectedConversation 
                        ? selectedConversation.user?.email 
                        : `${conversations.length} conversation${conversations.length !== 1 ? 's' : ''}`
                      }
                    </p>
                  </div>
                </div>
                <button onClick={() => { setIsOpen(false); setSelectedConversation(null); }} className="p-2 hover:bg-babylon-gold/10 rounded-lg">
                  <X size={18} className="text-babylon-text-dim" />
                </button>
              </div>
              
              {/* Content */}
              <div className="flex-1 overflow-hidden">
                {loading ? (
                  <div className="h-full flex items-center justify-center">
                    <Loader2 className="w-6 h-6 animate-spin text-babylon-gold" />
                  </div>
                ) : selectedConversation ? (
                  // Conversation View
                  <div className="h-full flex flex-col">
                    <ScrollArea className="flex-1 p-4">
                      <div className="space-y-3">
                        {messages.map((msg) => (
                          <motion.div
                            key={msg.id}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className={`flex ${msg.sender_type === 'admin' ? 'justify-end' : 'justify-start'}`}
                          >
                            <div className={`max-w-[80%] p-3 rounded-xl ${
                              msg.sender_type === 'admin' 
                                ? 'bg-babylon-gold/20 border border-babylon-gold/30' 
                                : 'bg-babylon-amethyst/10 border border-babylon-amethyst/20'
                            }`}>
                              {msg.subject && (
                                <p className="text-xs text-babylon-amethyst font-medium mb-1">{msg.subject}</p>
                              )}
                              <p className="text-sm text-babylon-text whitespace-pre-wrap">{msg.message}</p>
                              <div className="flex items-center gap-2 mt-2">
                                <Clock size={10} className="text-babylon-text-dim" />
                                <span className="text-[10px] text-babylon-text-dim">{formatTimeAgo(msg.created_at)}</span>
                                {msg.sender_type === 'admin' && msg.read && (
                                  <CheckCheck size={10} className="text-babylon-gold" />
                                )}
                              </div>
                            </div>
                          </motion.div>
                        ))}
                        <div ref={messagesEndRef} />
                      </div>
                    </ScrollArea>
                    
                    {/* Reply Input */}
                    <div className="p-4 border-t border-babylon-gold/20 shrink-0">
                      <div className="flex gap-2">
                        <Textarea
                          placeholder="Type your reply..."
                          value={reply}
                          onChange={(e) => setReply(e.target.value)}
                          className="bg-babylon-void border-babylon-gold/20 text-sm min-h-[60px] max-h-[100px] resize-none flex-1"
                          maxLength={2000}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              handleReply();
                            }
                          }}
                        />
                        <Button
                          onClick={handleReply}
                          disabled={sending || !reply.trim()}
                          className="btn-primary self-end"
                        >
                          {sending ? (
                            <Loader2 size={16} className="animate-spin" />
                          ) : (
                            <Send size={16} />
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : conversations.length === 0 ? (
                  // Empty Inbox
                  <div className="h-full flex flex-col items-center justify-center p-6 text-center">
                    <Mail className="w-16 h-16 text-babylon-gold/20 mb-4" />
                    <p className="text-babylon-text-dim">No messages yet</p>
                    <p className="text-babylon-text-dim text-xs mt-1">User messages will appear here</p>
                  </div>
                ) : (
                  // Conversation List
                  <ScrollArea className="h-full">
                    <div className="p-2">
                      {conversations.map((conv) => (
                        <motion.button
                          key={conv.id}
                          onClick={() => selectConversation(conv)}
                          className="w-full p-3 rounded-xl hover:bg-babylon-gold/10 transition-colors flex items-start gap-3 text-left mb-2"
                          whileHover={{ x: 4 }}
                        >
                          <Avatar className="w-10 h-10 border border-babylon-gold/30">
                            <AvatarImage src={getAvatarUrl(conv.user?.avatar_url)} />
                            <AvatarFallback className="bg-babylon-obsidian text-babylon-gold">
                              {conv.user?.name?.charAt(0) || 'U'}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2">
                              <p className="font-medium text-babylon-text truncate">{conv.user?.name || 'Unknown'}</p>
                              <span className="text-[10px] text-babylon-text-dim shrink-0">
                                {formatTimeAgo(conv.last_message_at)}
                              </span>
                            </div>
                            <p className="text-xs text-babylon-text-dim truncate">
                              {conv.last_message?.subject || conv.last_message?.message?.substring(0, 50) || 'No messages'}
                            </p>
                            {conv.admin_unread > 0 && (
                              <span className="inline-flex items-center gap-1 mt-1 text-[10px] text-babylon-blood">
                                <span className="w-2 h-2 bg-babylon-blood rounded-full"></span>
                                {conv.admin_unread} unread
                              </span>
                            )}
                          </div>
                        </motion.button>
                      ))}
                    </div>
                  </ScrollArea>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default AdminInbox;
